import { createContext, useState, ReactNode, useEffect } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { useAuth } from './AuthContext';
import {
  useGetStrategyByEventIdQuery,
  useGetStrategyMessagesQuery,
} from 'lib.store/src/ai-assintence/ai-assistence.api';
import { GetStrategyByIdResponse, StrategyMessageData } from 'lib.store/src/ai-assintence/models';
import { wsAuthEndpoint, wsHost } from 'lib.shared/src/api';

const pusherKey = import.meta.env.VITE_API_PUSHER_KEY as string;
const wsPort = import.meta.env.VITE_API_PUSHER_WS_PORT as string;
const wssPort = import.meta.env.VITE_API_PUSHER_WSS_PORT as string;
const cluster = import.meta.env.VITE_API_PUSHER_CLUSTER as string;
declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: Echo<'reverb' | 'pusher'>;
  }
}
interface AiAssintenceContextType {
  planGeneratedResponse: PlanGeneratedResponse | null;
  setPlanGeneratedResponse: (response: PlanGeneratedResponse | null) => void;
  conversationAnsweredResponse: string;
  setConversationAnsweredResponse: (response: string) => void;
  strategyData: GetStrategyByIdResponse['data'] | null;
  setStrategyData: (response: GetStrategyByIdResponse['data'] | null) => void;
  generatedPlanHtml: string;
  setGeneratedPlanHtml: (response: string) => void;
  isThinking: boolean;
  setIsThinking: (response: boolean) => void;
  thinkingMessage: Partial<StrategyMessageData> | null;
  setThinkingMessage: (response: Partial<StrategyMessageData> | null) => void;
  messagesHistory: StrategyMessageData[];
  setMessagesHistory: (response: StrategyMessageData[]) => void;
}
interface PlanGeneratedResponse {
  isStoppable: boolean;
  html: string;
  isSuccess: boolean;
}

export interface ThinkingResponse {
  user: {
    id: number;
    name: string;
    email: string;
    email_verified_at: string;
    phone: string;
    roles: string[];
    date: {
      created_at: string;
      updated_at: string;
    };
  };
  context: {
    agent: string;
    params: {
      event_id: number;
    };
  };
  status: {
    successful: boolean;
    stoppable: boolean;
  };
  html: string;
}

window.Pusher = Pusher;

window.Echo = new Echo({
  broadcaster: 'reverb',
  key: pusherKey,
  wsHost: wsHost,
  wsPort: wsPort,
  wssPort: wssPort,
  forceTLS: true,
  enabledTransports: ['ws', 'wss'],
  authEndpoint: wsAuthEndpoint,
  cluster: cluster,
  encrypted: true,
  disableStats: true,
});

export const AiAssintenceContext = createContext<AiAssintenceContextType>({
  planGeneratedResponse: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPlanGeneratedResponse: () => {},

  conversationAnsweredResponse: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setConversationAnsweredResponse: () => {},
  strategyData: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setStrategyData: () => {},
  generatedPlanHtml: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setGeneratedPlanHtml: () => {},

  isThinking: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsThinking: () => {},

  thinkingMessage: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setThinkingMessage: () => {},

  messagesHistory: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMessagesHistory: () => {},
});

interface AiAssintenceProviderProps {
  children: ReactNode;
}

export const AiAssintenceProvider: React.FC<AiAssintenceProviderProps> = ({ children }) => {
  const [planGeneratedResponse, setPlanGeneratedResponse] = useState<PlanGeneratedResponse | null>(
    null,
  );
  const [generatedPlanHtml, setGeneratedPlanHtml] = useState<string>('');
  const [isThinking, setIsThinking] = useState<boolean>(false);

  const [thinkingMessage, setThinkingMessage] = useState<Partial<StrategyMessageData> | null>(null);
  // const [thinkingResponse, setThinkingResponse] = useState<ThinkingResponse | null>(null);
  const [conversationAnsweredResponse, setConversationAnsweredResponse] = useState<string>('');
  const [strategyData, setStrategyData] = useState<GetStrategyByIdResponse['data'] | null>(null);
  const [messagesHistory, setMessagesHistory] = useState<StrategyMessageData[]>([]);

  const { data: strategyByEventIdData } = useGetStrategyByEventIdQuery({ eventId: '1' });
  const { data: generatedMessagesResponse } = useGetStrategyMessagesQuery({ eventId: '1' });

  const { user } = useAuth();
  const organizationId = user?.organization?.id || '';

  const onListenPlanGeneratedByOrganizationId = ({
    organizationId,
  }: {
    organizationId: string;
  }) => {
    window.Echo.private(`ai.agent.organization.${organizationId}`)
      .listen('.think', (response: ThinkingResponse) => {
        //TODO: remove this after testing
        console.log('listen think by organization id response');
        console.log(response);
        console.log('--------------------------------');
        setIsThinking(true);
        setThinkingMessage({
          id: 'thinking',
          parent_id: 0,
          user_id: 0,
          organization_id: 0,
          assistant_id: 0,
          thread_id: 0,
          message: response.html,
          prompt: 0,
          is_ai: 1,
        });
        // setThinkingResponse(response);
        // void message.success(response.html);
      })
      .listen('.conversation.answered', (response: PlanGeneratedResponse) => {
        //TODO: remove this after testing
        console.log('listen conversation.answered by organization id response');
        console.log(response);
        console.log('--------------------------------');
        if (response?.html) {
          setConversationAnsweredResponse(response.html);
          setIsThinking(false);
        }
      })
      .listen('.partner.strategy.generated', (response: PlanGeneratedResponse) => {
        //TODO: remove this after testing
        console.log('listen plan generated by organization id response');
        console.log(response);
        console.log('--------------------------------');

        setPlanGeneratedResponse(response);
        setGeneratedPlanHtml(response.html);
        setIsThinking(false);
      });
  };

  useEffect(() => {
    if (organizationId) {
      console.info('Listener for plan generated by organization id fired');
      onListenPlanGeneratedByOrganizationId({ organizationId: organizationId.toString() });
    }
  }, [organizationId]);

  useEffect(() => {
    if (strategyByEventIdData?.data) {
      setStrategyData(strategyByEventIdData.data);
    }
  }, [strategyByEventIdData]);

  useEffect(() => {
    if (generatedMessagesResponse?.data && generatedMessagesResponse.data.length > 0) {
      setMessagesHistory(generatedMessagesResponse.data);
    }
  }, [generatedMessagesResponse]);

  return (
    <AiAssintenceContext.Provider
      value={{
        planGeneratedResponse,
        setPlanGeneratedResponse,
        conversationAnsweredResponse,
        setConversationAnsweredResponse,
        strategyData,
        setStrategyData,
        generatedPlanHtml,
        setGeneratedPlanHtml,
        isThinking,
        setIsThinking,
        thinkingMessage,
        setThinkingMessage,
        messagesHistory,
        setMessagesHistory,
      }}
    >
      {children}
    </AiAssintenceContext.Provider>
  );
};
